import { createClient } from '@supabase/supabase-js';

const isBrowser = typeof window !== 'undefined';

const supabaseUrl =
  import.meta.env.VITE_SUPABASE_URL ||
  (typeof process !== 'undefined' ? (process.env.SUPABASE_URL || process.env.VITE_SUPABASE_URL) : undefined);

const supabaseAnonKey =
  import.meta.env.VITE_SUPABASE_ANON_KEY ||
  (typeof process !== 'undefined'
    ? (process.env.SUPABASE_ANON_KEY || process.env.VITE_SUPABASE_ANON_KEY)
    : undefined);

// In Node/server contexts (scripts, backend runtimes), prefer service role if present.
// This is never available in the browser bundle (unless someone misconfigures envs).
const supabaseServiceRoleKey = !isBrowser && typeof process !== 'undefined'
  ? (process.env.SUPABASE_SERVICE_ROLE_KEY || process.env.SUPABASE_SECRET || process.env.VITE_SUPABASE_SERVICE_ROLE_KEY)
  : undefined;

const supabaseKey = supabaseServiceRoleKey || supabaseAnonKey;

export type AuthStorageMode = 'local' | 'session';

const AUTH_STORAGE_MODE_KEY = 'rahsmatch_auth_storage_mode';

export function getAuthStorageMode(): AuthStorageMode {
  if (!isBrowser) return 'local';

  try {
    const raw = window.localStorage.getItem(AUTH_STORAGE_MODE_KEY);
    return raw === 'session' ? 'session' : 'local';
  } catch {
    return 'local';
  }
}

function clearSupabaseKeys(storage: Storage) {
  const keys: string[] = [];
  for (let i = 0; i < storage.length; i += 1) {
    const k = storage.key(i);
    if (k && k.startsWith('sb-')) keys.push(k);
  }
  for (const k of keys) storage.removeItem(k);
}

export function setAuthStorageMode(mode: AuthStorageMode) {
  if (!isBrowser) return;

  try {
    window.localStorage.setItem(AUTH_STORAGE_MODE_KEY, mode);
  } catch {
    // ignore
  }

  // Best-effort: clear any persisted Supabase auth keys from the *other* storage, so switching
  // between "remembered" and session-only sign-ins doesn't resurrect old sessions later.
  try {
    const other = mode === 'session' ? window.localStorage : window.sessionStorage;
    clearSupabaseKeys(other);
  } catch {
    // ignore
  }
}

type StorageLike = {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
  removeItem: (key: string) => void;
};

const authStorage: StorageLike = isBrowser
  ? {
      getItem: (key) => {
        const mode = getAuthStorageMode();
        const storage = mode === 'session' ? window.sessionStorage : window.localStorage;
        return storage.getItem(key);
      },
      setItem: (key, value) => {
        const mode = getAuthStorageMode();
        const storage = mode === 'session' ? window.sessionStorage : window.localStorage;
        storage.setItem(key, value);
      },
      removeItem: (key) => {
        // Remove from both storages to avoid drift.
        window.localStorage.removeItem(key);
        window.sessionStorage.removeItem(key);
      },
    }
  : {
      getItem: () => null,
      setItem: () => undefined,
      removeItem: () => undefined,
    };

export const supabase = createClient(supabaseUrl as string, supabaseKey as string, {
  auth: {
    persistSession: isBrowser,
    autoRefreshToken: isBrowser,
    detectSessionInUrl: isBrowser,
    storage: authStorage,
  },
});

export const isSupabaseConfigured = () => {
  return Boolean(supabaseUrl && supabaseKey);
};

export interface MarketingUser {
  id: string;
  email: string;
  role?: string;
  [key: string]: any;
}
